import React, { useState } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import {
    ChevronRight,
    ArrowLeft,
    Video,
    BarChart2,
    MessageSquare
} from 'lucide-react';

const GameFeedbackSidebar = ({ toggleMenu }) => {
    const navigate = useNavigate();
    const [isCollapsed, setIsCollapsed] = useState(false);

    const handleClickBackButton = () => {
        navigate("/game-posts/game-reports");
    };

    const navItems = [
        { to: "coach-feedback", icon: MessageSquare, label: "Coach Feedback" },
        { to: "highlights", icon: Video, label: "Highlights" },
        { to: "stats", icon: BarChart2, label: "Stats" },
    ];

    return (
        <aside
            className={`
                flex flex-col bg-white shadow-xl z-50 transition-all duration-300 ease-in-out h-full
                lg:sticky lg:top-0 lg:h-screen lg:z-50
                ${isCollapsed ? 'w-20' : 'w-full lg:w-72'}
            `}
        >
            {/* Toggle Button (Desktop) */}
            <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="absolute -right-3 top-1/2 -translate-y-1/2 bg-white border border-gray-200 rounded-full p-1 shadow-md hover:bg-gray-50 text-gray-600 z-50 hidden lg:flex"
                style={{
                    transition: 'background-color 200ms ease, transform 200ms ease',
                }}
            >
                <span
                    style={{
                        display: 'flex',
                        transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                    }}
                >
                    <ChevronRight size={16} />
                </span>
            </button>

            {/* Navigation Links */}
            <div className={`flex-1 overflow-y-auto scrollbar-hide px-3 space-y-2 pb-8 ${isCollapsed ? 'pt-24' : 'pt-8 lg:pt-28'}`}>
                {/* Back Button */}
                <button
                    onClick={handleClickBackButton}
                    className={`
                        w-full flex items-center gap-3 px-3 py-3 rounded-xl group transition-all duration-200 mb-6
                        text-gray-600 hover:bg-gray-50
                        ${isCollapsed ? 'justify-center' : ''}
                    `}
                >
                    <ArrowLeft size={22} className="flex-shrink-0 text-gray-500 group-hover:text-emerald-600 transition-colors" />
                    {!isCollapsed && (
                        <span className="font-medium whitespace-nowrap overflow-hidden transition-all duration-300 origin-left text-gray-700 group-hover:text-emerald-600">
                            Back to Reports
                        </span>
                    )}
                </button>

                {!isCollapsed && (
                    <div className="px-4 mb-4">
                        <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest opacity-50">Navigation</span>
                    </div>
                )}
                {navItems.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={toggleMenu}
                        className={({ isActive }) => `
                            sidebar-link
                            relative flex items-center gap-3 px-3 py-3 rounded-xl group transition-all duration-200
                            ${isActive ? 'bg-secondary-color !text-primary-color font-medium shadow-sm' : 'text-gray-600 hover:bg-secondary-color hover:!text-primary-color'}
                            ${isCollapsed ? 'justify-center' : ''}
                        `}
                    >
                        {({ isActive }) => (
                            <>
                                {/* Active Indicator Bar */}
                                <div className={`
                                    absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-7 bg-primary-color rounded-r-full transition-all duration-300 transform origin-left z-20
                                    ${isActive ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0'}
                                `} />

                                <item.icon
                                    size={22}
                                    className={`flex-shrink-0 transition-colors duration-200 relative z-10 ${isActive ? '!text-primary-color' : 'text-gray-500 group-hover:!text-primary-color'}`}
                                />

                                {!isCollapsed && (
                                    <span className="whitespace-nowrap overflow-hidden transition-all duration-300 origin-left relative z-10">
                                        {item.label}
                                    </span>
                                )}
                            </>
                        )}
                    </NavLink>
                ))}
            </div>
        </aside>
    );
};

export default GameFeedbackSidebar;